import { useState } from "react";
import { submitToWaitlist, validateEmail } from "../lib/supabase";

/**
 * useWaitlist
 * Manages waitlist form state, validation, and Supabase submission.
 *
 * Usage:
 *   const { fields, setField, status, errors, handleSubmit, reset } = useWaitlist();
 *
 * Status values:
 *   "idle" | "loading" | "success" | "duplicate" | "error"
 */
export function useWaitlist() {
  const [fields, setFields] = useState({ email: "", firstName: "" });
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");

  // Update a single field and clear its error
  const setField = (name, value) => {
    setFields((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (serverError) setServerError("");
  };

  // Client-side validation before hitting Supabase
  const validate = () => {
    const errs = {};
    if (!fields.email.trim()) {
      errs.email = "Email address is required.";
    } else if (!validateEmail(fields.email)) {
      errs.email = "Please enter a valid email address.";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Main submit handler — calls Supabase insert
  const handleSubmit = async () => {
    if (status === "loading") return;
    if (!validate()) return;

    setStatus("loading");
    setServerError("");

    const { error, isDuplicate } = await submitToWaitlist({
      email: fields.email,
      firstName: fields.firstName || null,
    });

    if (isDuplicate) {
      setStatus("duplicate");
      return;
    }

    if (error) {
      setStatus("error");
      setServerError(error.message || "Something went wrong. Please try again.");
      return;
    }
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-waitlist-email`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "apikey": process.env.REACT_APP_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          email: fields.email,
          firstName: fields.firstName || ""
        })
      }
    );

    const data = await res.json();
    console.log("Email function response:", data);
    setStatus("success");
  };

  const reset = () => {
    setFields({ email: "", firstName: "" });
    setStatus("idle");
    setErrors({});
    setServerError("");
  };

  return {
    fields,
    setField,
    status,
    errors,
    serverError,
    handleSubmit,
    reset,
    isLoading: status === "loading",
    isSuccess: status === "success",
    isDuplicate: status === "duplicate",
    isError: status === "error",
  };
}
